// STATYSTYKA — MEDIANA (n parzyste/nieparzyste), bez Σ w wyjaśnieniu
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// helpers
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }
function mixedLatex(p, q) {
  let [pp, qq] = simplify(p, q); const neg = pp < 0; pp = Math.abs(pp);
  const whole = Math.floor(pp/qq), rem = pp%qq;
  if (whole===0 && rem===0) return "0";
  if (whole===0) return (neg?"-":"")+`\\dfrac{${rem}}{${qq}}`;
  if (rem===0) return (neg?"-":"")+`${whole}`;
  return (neg?"-":"")+`${whole}\\; ${dfrac(rem,qq)}`;
}
function parseFraction(raw) {
  if (!raw) return null; const s0 = String(raw).trim(); if (!s0) return null;
  let m = s0.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/); if (m) { const den = parseInt(m[2],10); if (!den) return null; return [parseInt(m[1],10), den]; }
  if (/^[+-]?\d+$/.test(s0)) return [parseInt(s0,10),1];
  const s = s0.replace(",", "."); if (/^[+-]?(?:\d+|\d*\.\d+)$/.test(s)) {
    const sign = s.startsWith("-")?-1:1; const body = s.replace(/^[+-]/,""); const [ip,fp=""]=body.split(".");
    const k = fp.length; const p = sign * (parseInt(ip||"0",10)*(10**k) + parseInt(fp||"0",10)); const q = 10**k; return [p,q];
  }
  return null;
}
function shuffle(a){ for(let i=a.length-1;i>0;i--){ const j=Math.floor(Math.random()*(i+1)); [a[i],a[j]]=[a[j],a[i]]; } return a; }

// ========== GENERATOR ==========
export function generateTask() {
  const n = 7 + Math.floor(Math.random()*6); // 7..12 (mamy i parzyste, i nieparzyste)
  const data = Array.from({length:n}, () => 1 + Math.floor(Math.random()*20)); // 1..20
  const sorted = [...data].sort((a,b)=>a-b);
  let p=0,q=1; // mediana jako ułamek p/q
  if (n % 2 === 1) {
    const mid = sorted[(n-1)/2]; p = mid; q = 1;
  } else {
    const a = sorted[n/2 - 1], b = sorted[n/2]; p = a + b; q = 2;
  }
  return {
    type: "MedianTask",
    data: { n, data, sorted, p, q },
    uid: `median_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

// ========== TREŚĆ ==========
export function renderTask({ data }) {
  const list = shuffle([...data.data]); // pokazuj nieuporządkowane
  const listLatex = list.join(",\\,");
  return (
    <div style={{textAlign:'left', lineHeight:1.6}}>
      <div>Podane dane (nieuporządkowane): <InlineMath math={listLatex} /><span>.</span></div>
      <div style={{marginTop:4}}>
        Oblicz <b>medianę</b> <InlineMath math={'\\mathrm{Me}'} /><span>.</span>
      </div>
    </div>
  );
}

// ========== INPUT ==========
export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3" style={{marginTop:6}}>
      <span><InlineMath math={'\\mathrm{Me} = '} /></span>
      <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label={"\\mathrm{Me}"} placeholder="np. 8, 9/2" />
    </div>
  );
}

// ========== CHECK ==========
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || !userAnswer.raw) return { correct:false, message:"Podaj wartość mediany." };
  const parsed = parseFraction(userAnswer.raw); if (!parsed) return { correct:false, message:"Nieprawidłowy zapis." };
  const uv = parsed[0]/parsed[1]; const corr = data.p / data.q;
  const correct = Math.abs(uv - corr) <= 0.01 + 1e-12;
  const imp = dfrac(data.p, data.q), mix = mixedLatex(data.p, data.q);
  const expected = data.q===1 ? `\\mathrm{Me} = ${imp}` : `\\mathrm{Me} = ${imp} \\;(=\\; ${mix})`;
  return { correct, expected };
}

// ========== ODPOWIEDŹ / PODGLĄD ==========
export function renderAnswer({ data }) {
  const imp = dfrac(data.p, data.q), mix = mixedLatex(data.p, data.q);
  return <InlineMath math={`\\mathrm{Me} = ${imp}${data.q===1?'':`\\; (=\\; ${mix})`}`} />;
}
export function renderUserAnswer(ans) {
  if (!ans || !ans.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(ans.raw, '\\\\mathrm{Me}')}/>;
}

// ========== WYJAŚNIENIE ==========
export function renderExplanation({ data }) {
  const n = data.n;
  const unsorted = data.data.join(",\\,");
  const sorted = data.sorted.join(",\\,");
  const imp = dfrac(data.p, data.q), mix = mixedLatex(data.p, data.q);

  return (
    <div style={{textAlign:'left', lineHeight:1.75}}>
      <div><strong>Krok 1 — porządkujemy rosnąco</strong></div>
      <BlockMath math={`\\text{było:}\\; ${unsorted}`} />
      <BlockMath math={`\\text{po sortowaniu:}\\; ${sorted}`} />

      {n%2===1 ? (
        <>
          <div style={{marginTop:8}}><strong>Krok 2 — środkowy wyraz (n nieparzyste)</strong></div>
          <BlockMath math={`\\mathrm{Me} = a_{\\tfrac{${n}+1}{2}} = ${imp}`} />
        </>
      ) : (
        <>
          <div style={{marginTop:8}}><strong>Krok 2 — średnia dwóch środkowych (n parzyste)</strong></div>
          <BlockMath math={`\\mathrm{Me} = \\dfrac{a_{\\tfrac{${n}}{2}} + a_{\\tfrac{${n}}{2}+1}}{2} = ${imp}${data.q===1?'':`\\; (=\\; ${mix})`}`} />
        </>
      )}
    </div>
  );
}
